import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { slaApi, inventoryApi, walletApi, locationsApi, shippingApi, storeConnectionsApi, carriersApi, CarrierWithMethods, customsSettingsApi, CustomsSettings, TaxIdentifier, organizationApi, printAgentApi, teamApi } from '../services/api';
import { AddFundsModal } from '../components/AddFundsModal';
import {
  TruckIcon,
  BuildingStorefrontIcon,
  GlobeAltIcon,
  ShoppingCartIcon,
  BuildingOfficeIcon,
  LinkIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import * as printerService from '../services/printer';
import { PackageSettings } from '../components/PackageSettings';

interface StoreConnection {
  id: string;
  platform: string;
  store_name: string;
  store_url: string;
  status: 'active' | 'error' | 'pending';
  last_sync_at: string | null;
  last_sync_status: string | null;
  orders_synced_count: number;
  created_at: string;
  sync_settings?: {
    orders_source?: 'shopify' | 'faire';
  };
  orders_source_toggle_available?: boolean;
}

export const Settings: React.FC = () => {
  // Get initial section from URL hash or default to 'locations'
  const getInitialSection = () => {
    const hash = window.location.hash.replace('#', '');
    const validSections = ['carriers', 'store', 'wallet', 'locations', 'packages', 'international', 'printers', 'team', 'advanced'];
    return validSections.includes(hash) ? hash as typeof activeSection : 'locations';
  };
  
  const [activeSection, setActiveSectionState] = useState<'carriers' | 'store' | 'wallet' | 'locations' | 'packages' | 'international' | 'printers' | 'team' | 'advanced'>(getInitialSection);
  
  // Wrapper to also update URL hash when section changes
  const setActiveSection = (section: typeof activeSection) => {
    setActiveSectionState(section);
    window.history.replaceState(null, '', `#${section}`);
  };

  // Use React Router's location to detect hash changes
  const location = useLocation();

  // Update active section when URL hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validSections = ['carriers', 'store', 'wallet', 'locations', 'packages', 'international', 'printers', 'team', 'advanced'];
    if (validSections.includes(hash)) {
      setActiveSectionState(hash as typeof activeSection);
    } else if (!hash) {
      // Default to locations if no hash
      setActiveSectionState('locations');
    }
  }, [location.hash]);

  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);

  // Team members
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);

  // Employee mode setting
  const [employeeModeEnabled, setEmployeeModeEnabled] = useState(false);

  // Organization timezone setting (account-level)
  const [orgTimezone, setOrgTimezone] = useState('America/New_York');
  const [orgTimezoneLoading, setOrgTimezoneLoading] = useState(false);
  const [maxPackageWeightLbs, setMaxPackageWeightLbs] = useState<number>(50);
  const [maxPackageWeightSaving, setMaxPackageWeightSaving] = useState(false);

  // International/Customs settings
  const [customsSettings, setCustomsSettings] = useState<CustomsSettings | null>(null);
  const [customsLoading, setCustomsLoading] = useState(false);
  const [showAddTaxId, setShowAddTaxId] = useState(false);
  const [newTaxId, setNewTaxId] = useState({
    type: 'vat' as TaxIdentifier['type'],
    value: '',
    issuing_authority: '',
    entity_type: 'shipper' as 'shipper' | 'recipient',
  });

  // Store connections (OAuth-based)
  const [storeConnections, setStoreConnections] = useState<StoreConnection[]>([]);
  const [shopifyDomain, setShopifyDomain] = useState('');
  const [showShopifyConnect, setShowShopifyConnect] = useState(false);
  const [showWooCommerceForm, setShowWooCommerceForm] = useState(false);
  const [showFaireForm, setShowFaireForm] = useState(false);
  const [faireAccessToken, setFaireAccessToken] = useState('');
  const [faireBrandName, setFaireBrandName] = useState('');
  const [faireOrdersSource, setFaireOrdersSource] = useState<'shopify' | 'faire'>('shopify');
  const [wooStoreUrl, setWooStoreUrl] = useState('');
  const [wooConsumerKey, setWooConsumerKey] = useState('');
  const [wooConsumerSecret, setWooConsumerSecret] = useState('');
  const [connectionMessage, setConnectionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Location form fields
  const [locationName, setLocationName] = useState('');
  const [locationCompany, setLocationCompany] = useState('');
  const [locationContact, setLocationContact] = useState('');
  const [locationPhone, setLocationPhone] = useState('');
  const [locationEmail, setLocationEmail] = useState('');
  const [locationAddress1, setLocationAddress1] = useState('');
  const [locationAddress2, setLocationAddress2] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [locationZip, setLocationZip] = useState('');

  // SLA settings
  const [slaEnabled, setSlaEnabled] = useState(true);
  const [shippingThreshold, setShippingThreshold] = useState(24);
  const [pickupThreshold, setPickupThreshold] = useState(12);

  // Inventory alert settings
  const [inventoryAlertsEnabled, setInventoryAlertsEnabled] = useState(true);
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  // Business hours settings
  const [useBusinessHours, setUseBusinessHours] = useState(false);
  const [businessDays, setBusinessDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri
  const [businessStartTime, setBusinessStartTime] = useState('09:00');
  const [businessEndTime, setBusinessEndTime] = useState('17:00');
  const [timezone, setTimezone] = useState('America/New_York');

  // Printer settings (stored per-computer in localStorage)
  const [printers, setPrinters] = useState<printerService.PrinterInfo[]>([]);
  const [printerSettings, setPrinterSettings] = useState<printerService.PrinterSettings>(
    printerService.getPrinterSettings()
  );
  const [printerLoading, setPrinterLoading] = useState(false);
  const [printerError, setPrinterError] = useState<string | null>(null);
  const [qzConnected, setQzConnected] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);
  const [printDevices, setPrintDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingExpiresAt, setPairingExpiresAt] = useState<string | null>(null);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [agentTestLoading, setAgentTestLoading] = useState(false);
  const [printMode, setPrintMode] = useState<'qz' | 'agent'>(
    printerService.getPrinterSettings().usePrintAgent ? 'agent' : 'qz'
  );

  // Emergency void settings
  const [voidTrackingNumber, setVoidTrackingNumber] = useState('');
  const [voidCarrier, setVoidCarrier] = useState<'ups' | 'usps'>('ups');

  // Carriers state
  const [carriers, setCarriers] = useState<CarrierWithMethods[]>([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [expandedCarrier, setExpandedCarrier] = useState<string | null>(null);
  const [showConnectUps, setShowConnectUps] = useState(false);
  const [connectingUps, setConnectingUps] = useState(false);
  const [upsAccountNumber, setUpsAccountNumber] = useState('');
  const [upsPostalCode, setUpsPostalCode] = useState('');
  const [upsAgreed, setUpsAgreed] = useState(false);
  const [carrierAccounts, setCarrierAccounts] = useState<any[]>([]);
  const [checkingConnection, setCheckingConnection] = useState(false);

  // Derived flags
  const hasActiveShopify = storeConnections.some(
    (conn) => conn.platform === 'shopify' && conn.status === 'active'
  );

  // Load SLA settings
  const loadTeam = async () => {
    setTeamLoading(true);
    try {
      const data = await teamApi.getTeam();
      setTeamMembers(data.members || []);
      setPendingInvites(data.pending_invites || []);
    } catch (error) {
      console.error('Failed to load team:', error);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const result: any = await teamApi.inviteUser(inviteEmail.trim(), inviteRole);
      const invitedEmail = inviteEmail;
      setInviteEmail('');
      setInviteRole('member');
      loadTeam();

      // If email failed (e.g. SendGrid quota exhausted) we still created the invite
      // and now surface the sign-up URL so the admin can share it manually.
      if (result?.email_sent === false && result?.sign_up_url) {
        const url = result.sign_up_url as string;
        try {
          await navigator.clipboard.writeText(url);
          toast.success(
            `Invite created for ${invitedEmail}, but email delivery failed (${result.email_error || 'unknown'}). Sign-up link copied to clipboard.`,
            { duration: 10000 }
          );
        } catch {
          toast.success(
            `Invite created for ${invitedEmail}, but email delivery failed. Copy the link from Pending Invites below.`,
            { duration: 10000 }
          );
        }
      } else {
        toast.success(`Invite sent to ${invitedEmail}`);
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to send invite';
      toast.error(msg);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteLink = async (signUpUrl: string) => {
    try {
      await navigator.clipboard.writeText(signUpUrl);
      toast.success('Sign-up link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await teamApi.cancelInvite(inviteId);
      toast.success('Invite cancelled');
      loadTeam();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to cancel invite');
    }
  };

  const loadSlaSettings = async () => {
    try {
      const settings = await slaApi.getSettings();
      setSlaEnabled(settings.enabled);
      setShippingThreshold(settings.shipping_threshold_hours);
      setPickupThreshold(settings.pickup_threshold_hours);
      setUseBusinessHours(settings.use_business_hours ?? false);
      setBusinessDays(settings.business_days ?? [1, 2, 3, 4, 5]);
      setBusinessStartTime(settings.business_start_time ?? '09:00');
      setBusinessEndTime(settings.business_end_time ?? '17:00');
      setTimezone(settings.timezone ?? 'America/New_York');
    } catch (error) {
      console.error('Failed to load SLA settings:', error);
    }
  };

  // Load Inventory alert settings
  const loadInventorySettings = async () => {
    try {
      const settings = await inventoryApi.getSettings();
      setInventoryAlertsEnabled(settings.enabled);
      setLowStockThreshold(settings.low_stock_threshold);
    } catch (error) {
      console.error('Failed to load inventory settings:', error);
    }
  };

  // Sync QZ connection badge with actual state (e.g. when opening Printers section)
  useEffect(() => {
    if (activeSection === 'printers') {
      setQzConnected(printerService.isQzConnected());
    }
  }, [activeSection]);

  // Auto-connect to QZ Tray if a label printer was previously configured
  const autoConnectQzTray = async () => {
    const settings = printerService.getPrinterSettings();
    if (settings.labelPrinter) {
      console.log('[Settings] Auto-connecting to QZ Tray (label printer previously configured)');
      try {
        await printerService.connectToQz();
        setQzConnected(true);
        const printerList = await printerService.getPrinters();
        setPrinters(printerList);
        console.log('[Settings] QZ Tray auto-connected, found', printerList.length, 'printers');
      } catch (error: any) {
        console.warn('[Settings] QZ Tray auto-connect failed:', error.message);
        // Don't show error - user can manually connect
      }
    }
  };

  const loadPrintDevices = async () => {
    setDevicesLoading(true);
    try {
      const data = await printAgentApi.listDevices();
      setPrintDevices(data.devices || []);
    } catch (error) {
      console.error('Failed to load print devices:', error);
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleRequestPairingCode = async () => {
    setPairingLoading(true);
    setPairingCode(null);
    setPairingExpiresAt(null);
    try {
      const platform = window.navigator.userAgent || 'unknown';
      const result = await printAgentApi.requestPairing('Workstation', platform);
      setPairingCode(result.pairing_code);
      setPairingExpiresAt(result.expires_at);
      await loadPrintDevices();
    } catch (error: any) {
      console.error('Failed to request pairing code:', error);
      alert(error.response?.data?.error || 'Failed to request pairing code');
    } finally {
      setPairingLoading(false);
    }
  };

  const handleSelectAgentDevice = (deviceId: string, printerName?: string | null) => {
    const newSettings = {
      ...printerSettings,
      usePrintAgent: true,
      agentDeviceId: deviceId,
      agentPrinterName: printerName || null,
    };
    setPrinterSettings(newSettings);
    setPrintMode('agent');
    printerService.savePrinterSettings(newSettings);
  };

  const handleRevokeDevice = async (deviceId: string) => {
    try {
      await printAgentApi.revokeDevice(deviceId);
      await loadPrintDevices();
    } catch (error: any) {
      console.error('Failed to revoke device:', error);
      alert(error.response?.data?.error || 'Failed to revoke device');
    }
  };

  const handleTestAgentPrint = async () => {
    if (!printerSettings.agentDeviceId) {
      alert('Select a print agent device first.');
      return;
    }
    setAgentTestLoading(true);
    try {
      // Tiny PDF (blank) base64
      const tinyPdf = 'JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDIgMCBSL1Jlc291cmNlczw8Pj4vTWVkaWFCb3hbMCAwIDYxMiA3OTJdL0NvbnRlbnRzIDQgMCBSPj4KZW5kb2JqCjQgMCBvYmoKWy9QREYgL1RleHQgMF0KZW5kb2JqCnhyZWYKMCA1CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDExMSAwMDAwMCBuIAowMDAwMDAwMDYxIDAwMDAwIG4gCjAwMDAwMDAxMTkgMDAwMDAgbiAKMDAwMDAwMDE5NyAwMDAwMCBuIAp0cmFpbGVyCjw8L1NpemUgNS9Sb290IDEgMCBSL0luZm8gNSAwIFI+PgpzdGFydHhyZWYKMjUxCiUlRU9G';
      await printAgentApi.createJob({
        device_id: printerSettings.agentDeviceId,
        label_data: tinyPdf,
        label_format: 'pdf',
        printer_name: printerSettings.agentPrinterName || undefined,
      });
      toast.success('Test print queued to agent');
    } catch (error: any) {
      console.error('Test print failed:', error);
      alert(error.response?.data?.error || 'Failed to queue test print');
    } finally {
      setAgentTestLoading(false);
    }
  };

  useEffect(() => {
    loadWalletBalance();
    loadLocations();
    loadStoreConnections();
    loadCarriers();
    loadCustomsSettings();
    loadCarrierAccounts();
    loadEmployeeMode();
    loadSlaSettings();
    loadInventorySettings();
    loadTeam();
    autoConnectQzTray();
    loadPrintDevices();

    // Handle OAuth callback success message
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected) {
      setConnectionMessage({ type: 'success', text: `Successfully connected to ${connected}!` });
      setActiveSection('store');
      // Clean up URL
      window.history.replaceState({}, '', '/settings');
      // Reload connections to show the new one
      loadStoreConnections();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCarrierAccounts = async () => {
    try {
      const result = await carriersApi.getAccounts();
      setCarrierAccounts(result.accounts || []);
    } catch (error) {
      console.error('Failed to load carrier accounts:', error);
    }
  };

  const loadEmployeeMode = async () => {
    try {
      const result = await organizationApi.getSettings();
      setEmployeeModeEnabled(result.employee_mode_enabled || false);
      setOrgTimezone(result.timezone || 'America/New_York');
      if (result.max_package_weight_lbs !== undefined) {
        setMaxPackageWeightLbs(result.max_package_weight_lbs);
      }
    } catch (error) {
      console.error('Failed to load employee mode setting:', error);
    }
  };

  const handleSaveOrgTimezone = async (newTimezone: string) => {
    setOrgTimezoneLoading(true);
    try {
      await organizationApi.updateSettings({ timezone: newTimezone });
      setOrgTimezone(newTimezone);
      toast.success('Timezone saved successfully');
    } catch (error: any) {
      console.error('Failed to save timezone:', error);
      toast.error(error.response?.data?.error || 'Failed to save timezone');
    } finally {
      setOrgTimezoneLoading(false);
    }
  };

  const handleSaveMaxPackageWeight = async () => {
    if (!maxPackageWeightLbs || maxPackageWeightLbs <= 0) {
      toast.error('Enter a positive weight');
      return;
    }
    setMaxPackageWeightSaving(true);
    try {
      await organizationApi.updateSettings({ max_package_weight_lbs: maxPackageWeightLbs });
      toast.success('Max package weight saved');
    } catch (error: any) {
      console.error('Failed to save max package weight', error);
      toast.error(error.response?.data?.error || 'Failed to save max package weight');
    } finally {
      setMaxPackageWeightSaving(false);
    }
  };

  const handleCheckUpsConnection = async (accountId: string) => {
    setCheckingConnection(true);
    try {
      const result = await carriersApi.checkUpsConnection(accountId);
      if (result.connected) {
        alert('UPS account connected successfully! Your negotiated rates will now appear.');
        await loadCarrierAccounts();
        await loadCarriers();
      } else {
        alert('Connection not yet complete. Please make sure you completed the UPS login.');
      }
    } catch (error: any) {
      alert(`Error checking connection: ${error.message}`);
    } finally {
      setCheckingConnection(false);
    }
  };

  const handleEnableNegotiatedRates = async (accountId: string) => {
    setCheckingConnection(true);
    try {
      const result = await carriersApi.enableNegotiatedRates(accountId);
      if (result.success) {
        alert('Negotiated rates enabled! Your UPS rates should now match your negotiated pricing.');
        await loadCarriers();
      } else {
        alert(`Could not enable negotiated rates: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert(`Error enabling negotiated rates: ${error.message}`);
    } finally {
      setCheckingConnection(false);
    }
  };

  const handleDisconnectCarrierAccount = async (accountId: string, accountLabel: string) => {
    const confirmed = window.confirm(
      `Disconnect ${accountLabel}? This will disable that carrier account until you reconnect it.`
    );
    if (!confirmed) return;

    setCheckingConnection(true);
    try {
      await carriersApi.disconnectAccount(accountId);
      await loadCarrierAccounts();
      await loadCarriers();
      toast.success('Carrier account disconnected');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'Failed to disconnect carrier account');
    } finally {
      setCheckingConnection(false);
    }
  };

  const loadCarriers = async () => {
    setCarriersLoading(true);
    try {
      const result = await carriersApi.list();
      setCarriers(result.carriers || []);
    } catch (error) {
      console.error('Failed to load carriers:', error);
    } finally {
      setCarriersLoading(false);
    }
  };

  const loadCustomsSettings = useCallback(async () => {
    setCustomsLoading(true);
    try {
      const result = await customsSettingsApi.get();
      setCustomsSettings(result.customs_settings);
    } catch (error) {
      console.error('Failed to load customs settings:', error);
    } finally {
      setCustomsLoading(false);
    }
  }, []);

  const handleUpdateCustomsSettings = async (updates: Partial<CustomsSettings>) => {
    try {
      const result = await customsSettingsApi.update(updates);
      setCustomsSettings(result.customs_settings);
    } catch (error: any) {
      alert(`Failed to update customs settings: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleAddTaxIdentifier = async () => {
    if (!newTaxId.value || !newTaxId.issuing_authority) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      await customsSettingsApi.addTaxIdentifier(newTaxId);
      await loadCustomsSettings();
      setShowAddTaxId(false);
      setNewTaxId({
        type: 'vat',
        value: '',
        issuing_authority: '',
        entity_type: 'shipper',
      });
    } catch (error: any) {
      alert(`Failed to add tax identifier: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleRemoveTaxIdentifier = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this tax identifier?')) return;
    try {
      await customsSettingsApi.removeTaxIdentifier(id);
      await loadCustomsSettings();
    } catch (error: any) {
      alert(`Failed to remove tax identifier: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleToggleCarrier = async (carrierId: string, currentEnabled: boolean) => {
    // Optimistic update - update UI immediately
    setCarriers(prev => prev.map(c => 
      c.id === carrierId ? { ...c, enabled: !currentEnabled } : c
    ));
    
    try {
      await carriersApi.toggleCarrier(carrierId, !currentEnabled);
      // Success - UI already updated
    } catch (error: any) {
      // Revert on error
      setCarriers(prev => prev.map(c => 
        c.id === carrierId ? { ...c, enabled: currentEnabled } : c
      ));
      alert(`Failed to toggle carrier: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleToggleMethod = async (methodId: string, currentEnabled: boolean) => {
    // Optimistic update - update UI immediately without collapsing
    setCarriers(prev => prev.map(carrier => ({
      ...carrier,
      methods: carrier.methods.map(m => 
        m.id === methodId ? { ...m, enabled: !currentEnabled } : m
      )
    })));
    
    try {
      await carriersApi.toggleMethod(methodId, !currentEnabled);
      // Success - UI already updated
    } catch (error: any) {
      // Revert on error
      setCarriers(prev => prev.map(carrier => ({
        ...carrier,
        methods: carrier.methods.map(m => 
          m.id === methodId ? { ...m, enabled: currentEnabled } : m
        )
      })));
      alert(`Failed to toggle method: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSetDefaultCarrier = async (carrierId: string) => {
    try {
      await carriersApi.setDefaultCarrier(carrierId);
      await loadCarriers();
    } catch (error: any) {
      alert(`Failed to set default carrier: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleConnectUpsAccount = async () => {
    // Validate required fields
    if (!upsAccountNumber || !upsPostalCode) {
      alert('Please enter your UPS account number and postal code');
      return;
    }

    if (!upsAgreed) {
      alert('You must agree to the UPS Technology Agreement to continue');
      return;
    }

    setConnectingUps(true);
    try {
      // ShipEngine only needs these 4 fields for UPS connection
      const accountInfo = {
        nickname: `UPS ${upsAccountNumber}`,
        account_number: upsAccountNumber,
        postal_code: upsPostalCode,
        country_code: 'US',
        agree_to_technology_agreement: true,
      };

      const result = await carriersApi.connectUps(accountInfo);
      
      if (result.success) {
        // Check if OAuth redirect is required
        if (result.requires_oauth && result.redirect_url) {
          // Open UPS login in a new window
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          const popup = window.open(
            result.redirect_url,
            'UPS Login',
            `width=${width},height=${height},left=${left},top=${top}`
          );
          
          // Poll for completion
          const accountId = result.account_id;
          let pollCount = 0;
          const maxPolls = 60; // Poll for up to 5 minutes (60 * 5s)
          
          const pollForCompletion = setInterval(async () => {
            pollCount++;
            
            // Check if popup was closed
            if (popup?.closed) {
              // Give it a couple more seconds after close
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              // Check if account is now connected
              try {
                const checkResult = await carriersApi.checkUpsConnection(accountId || '');
                if (checkResult.connected) {
                  clearInterval(pollForCompletion);
                  alert('UPS account connected successfully! Your negotiated rates will now appear when getting shipping rates.');
                  setShowConnectUps(false);
                  setUpsAccountNumber('');
                  setUpsPostalCode('');
                  setUpsAgreed(false);
                  await loadCarriers();
                  return;
                }
              } catch (e) {
                console.error('Error checking UPS connection:', e);
              }
              
              clearInterval(pollForCompletion);
              alert('UPS login window closed. If you completed the login, please refresh the page to see your connected account.');
              setShowConnectUps(false);
              await loadCarriers();
              return;
            }
            
            if (pollCount >= maxPolls) {
              clearInterval(pollForCompletion);
              setShowConnectUps(false);
            }
          }, 5000);
          
          setUpsAccountNumber('');
          setUpsPostalCode('');
          setUpsAgreed(false);
        } else {
          alert('UPS account connected successfully! Your negotiated rates will now appear when getting shipping rates.');
          setShowConnectUps(false);
          setUpsAccountNumber('');
          setUpsPostalCode('');
          setUpsAgreed(false);
          await loadCarriers();
        }
      } else {
        alert(`Failed to connect UPS account: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert(`Failed to connect UPS account: ${error.response?.data?.error || error.message}`);
    } finally {
      setConnectingUps(false);
    }
  };

  const loadStoreConnections = async () => {
    try {
      const result = await storeConnectionsApi.list();
      const connections = result.connections || [];
      setStoreConnections(connections);

      // Default Faire orders source: Shopify if connected, otherwise Faire direct
      const hasShopify = connections.some(
        (conn: StoreConnection) => conn.platform === 'shopify' && conn.status === 'active'
      );
      setFaireOrdersSource(hasShopify ? 'shopify' : 'faire');
    } catch (error) {
      console.error('Failed to load store connections:', error);
    }
  };

  const handleUpdateFaireOrdersSource = async (
    connectionId: string,
    source: 'shopify' | 'faire'
  ) => {
    setLoading(true);
    try {
      await storeConnectionsApi.updateSyncSettings(connectionId, { orders_source: source });
      await loadStoreConnections();
      setConnectionMessage({
        type: 'success',
        text: source === 'shopify'
          ? 'Faire orders will import from Shopify'
          : 'Faire orders will import directly from Faire',
      });
    } catch (error: any) {
      setConnectionMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update Faire import source',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadWalletBalance = async () => {
    try {
      const result = await walletApi.getBalance();
      setWalletBalance(parseFloat(result.balance?.balance || 0));
    } catch (error) {
      console.error('Failed to load wallet balance:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const result = await locationsApi.list();
      setLocations(result.locations || []);
      setShowLocationForm(result.locations?.length === 0);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const resetLocationForm = () => {
    setLocationName('');
    setLocationCompany('');
    setLocationContact('');
    setLocationPhone('');
    setLocationEmail('');
    setLocationAddress1('');
    setLocationAddress2('');
    setLocationCity('');
    setLocationState('');
    setLocationZip('');
    setEditingLocation(null);
  };

  const handleSaveLocation = async () => {
    if (!locationName || !locationCompany || !locationAddress1 || !locationCity || !locationState || !locationZip) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const locationData = {
        name: locationName,
        company_name: locationCompany,
        contact_name: locationContact,
        phone: locationPhone,
        email: locationEmail,
        address_line1: locationAddress1,
        address_line2: locationAddress2,
        city: locationCity,
        state: locationState,
        zip: locationZip,
        country: 'US',
        is_default: locations.length === 0, // First location is default
      };

      if (editingLocation) {
        await locationsApi.update(editingLocation.id, locationData);
        alert('Location updated successfully!');
      } else {
        await locationsApi.create(locationData);
        alert('Location created successfully!');
      }

      resetLocationForm();
      setShowLocationForm(false);
      await loadLocations();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.error || 'Failed to save location'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditLocation = (location: any) => {
    setLocationName(location.name);
    setLocationCompany(location.company_name);
    setLocationContact(location.contact_name || '');
    setLocationPhone(location.phone || '');
    setLocationEmail(location.email || '');
    setLocationAddress1(location.address_line1);
    setLocationAddress2(location.address_line2 || '');
    setLocationCity(location.city);
    setLocationState(location.state);
    setLocationZip(location.zip);
    setEditingLocation(location);
    setShowLocationForm(true);
  };

  const handleDeleteLocation = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this location? This cannot be undone.')) return;

    setLoading(true);
    try {
      await locationsApi.delete(id);
      alert('Location deleted successfully');
      await loadLocations();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.error || 'Failed to delete location'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    setLoading(true);
    try {
      await locationsApi.setDefault(id);
      await loadLocations();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.error || 'Failed to set default'}`);
    } finally {
      setLoading(false);
    }
  };

  // OAuth Store Connection Handlers
  const handleConnectShopify = async () => {
    if (!shopifyDomain) {
      alert('Please enter your Shopify store domain');
      return;
    }

    setLoading(true);
    try {
      // Clean up the domain (remove .myshopify.com if present)
      const cleanDomain = shopifyDomain.replace('.myshopify.com', '').replace(/^https?:\/\//, '');
      const result = await storeConnectionsApi.connect('shopify', { shop: cleanDomain });
      
      if (result.redirect_url) {
        // Redirect to Shopify OAuth
        window.location.href = result.redirect_url;
      } else {
        setConnectionMessage({ type: 'error', text: 'Failed to get redirect URL' });
      }
    } catch (error: any) {
      setConnectionMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to initiate Shopify connection' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectFaire = async () => {
    if (!faireAccessToken) {
      setConnectionMessage({ type: 'error', text: 'Please enter your Faire access token' });
      return;
    }

    setLoading(true);
    try {
      const result = await storeConnectionsApi.connect('faire', {
        access_token: faireAccessToken,
        brand_name: faireBrandName || undefined,
        orders_source: faireOrdersSource,
      });
      
      if (result.success) {
        setConnectionMessage({ type: 'success', text: `Connected to ${result.store_name || 'Faire'}!` });
        setShowFaireForm(false);
        setFaireAccessToken('');
        setFaireBrandName('');
        await loadStoreConnections();
      } else if (result.redirect_url) {
        // OAuth fallback (if configured)
        window.location.href = result.redirect_url;
      } else {
        setConnectionMessage({ type: 'error', text: result.error || 'Failed to connect' });
      }
    } catch (error: any) {
      setConnectionMessage({ 
        type: 'error', 
        text: error.response?.data?.error || error.response?.data?.message || 'Failed to connect to Faire' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectWooCommerce = async () => {
    if (!wooStoreUrl || !wooConsumerKey || !wooConsumerSecret) {
      alert('Please fill in all WooCommerce fields');
      return;
    }

    setLoading(true);
    try {
      const result = await storeConnectionsApi.connect('woocommerce', {
        store_url: wooStoreUrl,
        consumer_key: wooConsumerKey,
        consumer_secret: wooConsumerSecret,
      });

      if (result.success) {
        setConnectionMessage({ type: 'success', text: `Connected to ${result.store_name || 'WooCommerce store'}!` });
        setShowWooCommerceForm(false);
        setWooStoreUrl('');
        setWooConsumerKey('');
        setWooConsumerSecret('');
        await loadStoreConnections();
      } else {
        setConnectionMessage({ type: 'error', text: result.error || 'Failed to connect' });
      }
    } catch (error: any) {
      setConnectionMessage({ 
        type: 'error', 
        text: error.response?.data?.error || error.response?.data?.details || 'Failed to connect to WooCommerce' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectStore = async (connectionId: string, storeName: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Disconnect from ${storeName}? This will not delete existing synced orders.`)) return;

    setLoading(true);
    try {
      await storeConnectionsApi.disconnect(connectionId);
      setConnectionMessage({ type: 'success', text: `Disconnected from ${storeName}` });
      await loadStoreConnections();
    } catch (error: any) {
      setConnectionMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to disconnect' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncStore = async (connectionId: string, storeName: string) => {
    setLoading(true);
    try {
      const result = await storeConnectionsApi.sync(connectionId);
      setConnectionMessage({ 
        type: 'success', 
        text: `Synced ${result.orders_synced || 0} orders from ${storeName}` 
      });
      await loadStoreConnections();
    } catch (error: any) {
      setConnectionMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to sync orders' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (connectionId: string) => {
    setLoading(true);
    try {
      const result = await storeConnectionsApi.test(connectionId);
      if (result.success) {
        setConnectionMessage({ type: 'success', text: result.message || 'Connection test passed!' });
      } else {
        setConnectionMessage({ type: 'error', text: result.error || 'Connection test failed' });
      }
      await loadStoreConnections();
    } catch (error: any) {
      setConnectionMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Connection test failed' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetWebhooks = async (connectionId: string, storeName: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Reset ALL webhooks for ${storeName}?\n\nThis will delete all existing webhooks and re-register them.\nUse this if webhook sync is failing.`)) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await storeConnectionsApi.resetWebhooks(connectionId);
      setConnectionMessage({ 
        type: 'success', 
        text: `Webhooks reset: deleted ${result.deleted}, registered ${result.registered?.length || 0}` 
      });
    } catch (error: any) {
      setConnectionMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to reset webhooks' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform: string): React.ReactNode => {
    const iconClass = "w-5 h-5";
    switch (platform) {
      case 'shopify': return <ShoppingCartIcon className={iconClass} />;
      case 'faire': return <BuildingStorefrontIcon className={iconClass} />;
      case 'woocommerce': return <LinkIcon className={iconClass} />;
      default: return <BuildingOfficeIcon className={iconClass} />;
    }
  };

  const getPlatformColor = (platform: string): string => {
    switch (platform) {
      case 'shopify': return 'bg-green-100';
      case 'faire': return 'bg-purple-100';
      case 'woocommerce': return 'bg-indigo-100';
      default: return 'bg-slate-100';
    }
  };

  const handleSaveSLA = async () => {
    setLoading(true);
    try {
      await slaApi.updateSettings({
        enabled: slaEnabled,
        shipping_threshold_hours: shippingThreshold,
        pickup_threshold_hours: pickupThreshold,
        use_business_hours: useBusinessHours,
        business_days: businessDays,
        business_start_time: businessStartTime,
        business_end_time: businessEndTime,
        timezone: timezone,
      });
      alert('SLA settings saved successfully!');
    } catch (error: any) {
      alert(`Failed to save SLA settings: ${error.response?.data?.error || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInventorySettings = async () => {
    setLoading(true);
    try {
      await inventoryApi.updateSettings({
        enabled: inventoryAlertsEnabled,
        low_stock_threshold: lowStockThreshold,
      });
      alert('Inventory alert settings saved successfully!');
    } catch (error: any) {
      alert(`Failed to save inventory settings: ${error.response?.data?.error || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyVoid = async () => {
    if (!voidTrackingNumber) {
      alert('Please enter a tracking number');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Void label with ${voidCarrier.toUpperCase()}?\n\nTracking: ${voidTrackingNumber}\n\nThis will cancel the label with the carrier to prevent charges.`)) {
      return;
    }

    setLoading(true);
    try {
      const result = await shippingApi.voidByTrackingNumber(voidTrackingNumber, voidCarrier);
      if (result.success) {
        alert(`Success! Label voided with ${voidCarrier.toUpperCase()}.\n\n${result.message}`);
        setVoidTrackingNumber('');
      } else {
        alert(`Failed to void label:\n\n${result.message}`);
      }
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to void label'}`);
    } finally {
      setLoading(false);
    }
  };

  // Section titles and descriptions for cleaner headers
  const sectionInfo: Record<string, { title: string; description: string }> = {
    locations: { 
      title: 'Ship From Locations', 
      description: 'Set up your warehouse or store addresses. These will appear on shipping labels as the sender.' 
    },
    carriers: { 
      title: 'Carriers', 
      description: 'Connect your shipping carrier accounts and manage available shipping methods.' 
    },
    store: { 
      title: 'Store Connections', 
      description: 'Connect your e-commerce stores to automatically import orders.' 
    },
    wallet: { 
      title: 'Wallet & Billing', 
      description: 'Manage your shipping wallet balance and payment methods.' 
    },
    packages: {
      title: 'Package Sizes',
      description: 'Configure box sizes and weights used for shipping.'
    },
    international: { 
      title: 'International Shipping', 
      description: 'Configure customs defaults and tax identifiers for international shipments.' 
    },
    printers: { 
      title: 'Printers', 
      description: 'Configure label and packing slip printers for this workstation.' 
    },
    team: {
      title: 'Team Members',
      description: 'Invite users to your organization and manage team access.'
    },
    advanced: { 
      title: 'Advanced Settings', 
      description: 'SLA monitoring, employee mode, and troubleshooting tools.' 
    },
  };

  const currentSection = sectionInfo[activeSection] || sectionInfo.locations;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">{currentSection.title}</h1>
          <p className="text-slate-600 mt-1">{currentSection.description}</p>
        </div>

        {/* Locations Section */}
        {activeSection === 'locations' && (
          <div className="space-y-6">

            {/* Location List */}
            {locations.map((loc) => (
              <div key={loc.id} className="bg-white rounded-xl border-2 border-slate-200 shadow-subtle">
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <ArchiveBoxIcon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{loc.name}</h3>
                        <p className="text-sm text-slate-600">{loc.company_name}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {loc.city}, {loc.state} {loc.zip}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {loc.is_default && (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Default
                        </span>
                      )}
                      <button
                        onClick={() => handleEditLocation(loc)}
                        className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
                      >
                        Edit
                      </button>
                      {!loc.is_default && (
                        <button
                          onClick={() => handleSetDefault(loc.id)}
                          className="px-4 py-2 text-sm font-medium text-brand-blue border border-brand-blue rounded-lg hover:bg-blue-50"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteLocation(loc.id)}
                        className="px-4 py-2 text-sm font-medium text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-600 font-medium mb-1">Address</p>
                      <p className="text-slate-900">{loc.address_line1}</p>
                      {loc.address_line2 && <p className="text-slate-900">{loc.address_line2}</p>}
                      <p className="text-slate-900">{loc.city}, {loc.state} {loc.zip}</p>
                    </div>
                    <div>
                      {loc.contact_name && (
                        <div className="mb-2">
                          <p className="text-slate-600 font-medium">Contact</p>
                          <p className="text-slate-900">{loc.contact_name}</p>
                        </div>
                      )}
                      {loc.phone && (
                        <div className="mb-2">
                          <p className="text-slate-600 font-medium">Phone</p>
                          <p className="text-slate-900">{loc.phone}</p>
                        </div>
                      )}
                      {loc.email && (
                        <div>
                          <p className="text-slate-600 font-medium">Email</p>
                          <p className="text-slate-900">{loc.email}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add Location Button */}
            {!showLocationForm && (
              <button
                onClick={() => {
                  resetLocationForm();
                  setShowLocationForm(true);
                }}
                className="w-full py-6 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 hover:border-brand-blue hover:text-brand-blue hover:bg-blue-50 transition-colors"
              >
                + Add New Location
              </button>
            )}

            {/* Location Form */}
            {showLocationForm && (
              <div className="bg-white rounded-xl border-2 border-brand-blue shadow-subtle">
                <div className="px-6 py-4 border-b border-slate-200 bg-blue-50">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {editingLocation ? 'Edit Location' : 'Add New Location'}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    This address will appear on shipping labels as the sender
                  </p>
                </div>
                <div className="px-6 py-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Location Name *
                      </label>
                      <input
                        type="text"
                        value={locationName}
                        onChange={(e) => setLocationName(e.target.value)}
                        placeholder="Main Warehouse"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                      <p className="text-xs text-slate-500 mt-1">For your reference only</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        value={locationCompany}
                        onChange={(e) => setLocationCompany(e.target.value)}
                        placeholder="Peach State Candle Supply"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                      <p className="text-xs text-slate-500 mt-1">Appears on shipping label</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Contact Name
                      </label>
                      <input
                        type="text"
                        value={locationContact}
                        onChange={(e) => setLocationContact(e.target.value)}
                        placeholder="John Doe"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={locationPhone}
                        onChange={(e) => setLocationPhone(e.target.value)}
                        placeholder="(404) 555-1234"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={locationEmail}
                        onChange={(e) => setLocationEmail(e.target.value)}
                        placeholder="shipping@example.com"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Address Line 1 *
                    </label>
                    <input
                      type="text"
                      value={locationAddress1}
                      onChange={(e) => setLocationAddress1(e.target.value)}
                      placeholder="123 Main Street"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={locationAddress2}
                      onChange={(e) => setLocationAddress2(e.target.value)}
                      placeholder="Suite 100"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        value={locationCity}
                        onChange={(e) => setLocationCity(e.target.value)}
                        placeholder="Atlanta"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        value={locationState}
                        onChange={(e) => setLocationState(e.target.value)}
                        placeholder="GA"
                        maxLength={2}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        value={locationZip}
                        onChange={(e) => setLocationZip(e.target.value)}
                        placeholder="30331"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-slate-200">
                    <button
                      onClick={handleSaveLocation}
                      disabled={loading}
                      className="px-6 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : editingLocation ? 'Update Location' : 'Add Location'}
                    </button>
                    <button
                      onClick={() => {
                        resetLocationForm();
                        setShowLocationForm(false);
                      }}
                      className="px-6 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Carriers Section */}
        {activeSection === 'carriers' && (
          <div className="space-y-6">
            {/* ShipEngine Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <TruckIcon className="w-4 h-4 text-blue-700" />
                <p className="text-sm text-blue-900 font-medium">Shipping Powered by ShipEngine</p>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                All shipping rates and labels are powered by ShipEngine, giving you access to discounted rates from multiple carriers.
                Toggle carriers and shipping methods on/off to control which options appear when getting rates.
              </p>
            </div>

            {carriersLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
                <p className="text-sm text-slate-600 mt-2">Loading carriers...</p>
              </div>
            ) : carriers.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-8 text-center">
                <p className="text-slate-600">No carriers configured yet.</p>
                <button
                  onClick={loadCarriers}
                  className="mt-4 px-4 py-2 text-sm font-medium text-brand-blue border border-brand-blue rounded-lg hover:bg-blue-50"
                >
                  Refresh
                </button>
              </div>
            ) : (
              <>
                {/* Dynamic Carrier List */}
                {carriers.map((carrier) => {
                  const isExpanded = expandedCarrier === carrier.id;
                  const carrierBgColor = carrier.carrier_code === 'ups' ? 'bg-amber-100' :
                                         carrier.carrier_code === 'usps' ? 'bg-blue-100' :
                                         carrier.carrier_code === 'fedex' ? 'bg-purple-100' :
                                         carrier.carrier_code === 'dhl_express' ? 'bg-yellow-100' : 'bg-slate-100';
                  const carrierIconColor = carrier.carrier_code === 'ups' ? 'text-amber-700' :
                                           carrier.carrier_code === 'usps' ? 'text-blue-700' :
                                           carrier.carrier_code === 'fedex' ? 'text-purple-700' :
                                           carrier.carrier_code === 'dhl_express' ? 'text-yellow-700' : 'text-slate-600';

                  return (
                    <div key={carrier.id} className="bg-white rounded-xl border border-slate-200 shadow-subtle overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 ${carrierBgColor} rounded-lg flex items-center justify-center`}>
                              <TruckIcon className={`w-6 h-6 ${carrierIconColor}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h2 className="text-lg font-semibold text-slate-900">{carrier.carrier_name}</h2>
                                {carrier.carrier_source === 'customer' && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                                    Your Account
                                  </span>
                                )}
                                {carrier.is_default && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">
                                {carrier.methods.filter(m => m.enabled).length} of {carrier.methods.length} services enabled
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setExpandedCarrier(isExpanded ? null : carrier.id)}
                              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
                            >
                              {isExpanded ? '▼ Hide Services' : '▶ Show Services'}
                            </button>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={carrier.enabled}
                                onChange={() => handleToggleCarrier(carrier.id, carrier.enabled)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-blue rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-blue"></div>
                              <span className="ml-2 text-sm font-medium text-slate-700">
                                {carrier.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Services Section */}
                      {isExpanded && (
                        <div className="px-6 py-4 bg-slate-50">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-slate-700">Shipping Services</h3>
                            {!carrier.is_default && (
                              <button
                                onClick={() => handleSetDefaultCarrier(carrier.id)}
                                className="px-3 py-1 text-xs font-medium text-brand-blue border border-brand-blue rounded hover:bg-blue-50"
                              >
                                Set as Default
                              </button>
                            )}
                          </div>
                          <div className="space-y-2">
                            {carrier.methods.map((method) => (
                              <div
                                key={method.id}
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  method.enabled ? 'bg-white border border-slate-200' : 'bg-slate-100 border border-slate-100'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`w-2 h-2 rounded-full ${method.enabled ? 'bg-green-500' : 'bg-slate-400'}`}></span>
                                  <div>
                                    <p className={`text-sm font-medium ${method.enabled ? 'text-slate-900' : 'text-slate-500'}`}>
                                      {method.service_name}
                                    </p>
                                    <p className="text-xs text-slate-500">{method.service_code}</p>
                                  </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={method.enabled}
                                    onChange={() => handleToggleMethod(method.id, method.enabled)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-blue rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Connect Your Own UPS Account */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-2xl">
                      🔗
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">Connect Your Own UPS Account</h2>
                      <p className="text-sm text-slate-600">Use your negotiated UPS rates</p>
                    </div>
                  </div>
                  {!carrierAccounts.some(a => a.carrier_code === 'ups' && a.connection_status === 'active') && (
                    <button
                      onClick={() => setShowConnectUps(!showConnectUps)}
                      className="px-4 py-2 text-sm font-medium text-amber-700 border border-amber-600 rounded-lg hover:bg-amber-50"
                    >
                      {showConnectUps ? 'Cancel' : '+ Connect UPS Account'}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Show existing carrier accounts */}
              {carrierAccounts.filter(a => a.carrier_code === 'ups').map((account: any) => (
                <div key={account.id} className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{account.account_nickname || `UPS ${account.account_number}`}</p>
                      <p className="text-sm text-slate-600">Account: {account.account_number}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Status: {' '}
                        {account.connection_status === 'active' ? (
                          <span className="text-green-600 font-medium">✓ Connected</span>
                        ) : account.connection_status === 'awaiting_oauth' ? (
                          <span className="text-amber-600 font-medium">⏳ Awaiting Login Completion</span>
                        ) : account.connection_status === 'pending' ? (
                          <span className="text-blue-600 font-medium">⏳ Pending</span>
                        ) : (
                          <span className="text-red-600 font-medium">Error: {account.connection_error || 'Unknown'}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {account.connection_status === 'awaiting_oauth' && (
                        <button
                          onClick={() => handleCheckUpsConnection(account.id)}
                          disabled={checkingConnection}
                          className="px-4 py-2 text-sm font-medium text-amber-700 border border-amber-600 rounded-lg hover:bg-amber-50 disabled:opacity-50"
                        >
                          {checkingConnection ? 'Checking...' : 'Check Connection'}
                        </button>
                      )}
                      {account.connection_status === 'active' && (
                        <button
                          onClick={() => handleEnableNegotiatedRates(account.id)}
                          disabled={checkingConnection}
                          className="px-4 py-2 text-sm font-medium text-green-700 border border-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"
                        >
                          {checkingConnection ? 'Configuring...' : 'Enable Negotiated Rates'}
                        </button>
                      )}
                      {account.connection_status !== 'disconnected' && (
                        <button
                          onClick={() =>
                            handleDisconnectCarrierAccount(
                              account.id,
                              account.account_nickname || `UPS ${account.account_number}`
                            )
                          }
                          disabled={checkingConnection}
                          className="px-4 py-2 text-sm font-medium text-red-700 border border-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          {checkingConnection ? 'Working...' : 'Disconnect'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {showConnectUps && (
                <div className="px-6 py-5 bg-amber-50 border-t border-amber-200">
                  <div className="max-w-md">
                    <p className="text-sm text-slate-700 mb-4">
                      Enter your UPS account number and the ZIP code where it's registered. 
                      We'll use your ship-from location details for verification.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">UPS Account Number</label>
                        <input
                          type="text"
                          value={upsAccountNumber}
                          onChange={(e) => setUpsAccountNumber(e.target.value.toUpperCase())}
                          placeholder="e.g., 1A2B3C"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:outline-none"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Account ZIP Code</label>
                        <input
                          type="text"
                          value={upsPostalCode}
                          onChange={(e) => setUpsPostalCode(e.target.value)}
                          placeholder="e.g., 30301"
                          maxLength={10}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:outline-none"
                        />
                        <p className="text-xs text-slate-500 mt-1">The ZIP code where your UPS account is registered</p>
                      </div>
                      
                      <div className="flex items-start gap-2 pt-2">
                        <input
                          type="checkbox"
                          id="ups_agree"
                          checked={upsAgreed}
                          onChange={(e) => setUpsAgreed(e.target.checked)}
                          className="mt-1 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                        />
                        <label htmlFor="ups_agree" className="text-xs text-slate-600">
                          I agree to the <a href="https://www.ups.com/assets/resources/media/en_US/UTA.pdf" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">UPS Technology Agreement</a> and authorize Proship to use my UPS account for shipping.
                        </label>
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={() => {
                            setShowConnectUps(false);
                            setUpsAccountNumber('');
                            setUpsPostalCode('');
                            setUpsAgreed(false);
                          }}
                          className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConnectUpsAccount}
                          disabled={connectingUps || !upsAgreed || !upsAccountNumber || !upsPostalCode}
                          className="px-5 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {connectingUps ? (
                            <>
                              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Connecting...
                            </>
                          ) : 'Connect Account'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Info */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">How Pricing Works</h2>
                    <p className="text-sm text-slate-600">Simple, transparent shipping costs</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4">
                <ul className="text-sm text-slate-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Get real-time rates from enabled carriers when creating labels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Only enabled shipping methods will appear in rate results</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Label cost is deducted from your wallet balance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Access to discounted commercial rates</span>
                  </li>
                </ul>
                <button
                  onClick={() => setActiveSection('wallet')}
                  className="mt-4 px-6 py-2 text-sm font-medium text-brand-blue border border-brand-blue rounded-lg hover:bg-blue-50"
                >
                  Add Funds to Wallet →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Store & Integrations Section */}
        {activeSection === 'store' && (
          <div className="space-y-6">
            {/* Connection Success/Error Message */}
            {connectionMessage && (
              <div className={`rounded-lg p-4 ${
                connectionMessage.type === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-medium ${
                    connectionMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {connectionMessage.type === 'success' ? '✓' : '✗'} {connectionMessage.text}
                  </p>
                  <button
                    onClick={() => setConnectionMessage(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <BuildingStorefrontIcon className="w-4 h-4 text-blue-700" />
                <p className="text-sm text-blue-900 font-medium">Connect Your E-commerce Stores</p>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                Connect your online stores to automatically sync orders and upload tracking information.
              </p>
            </div>

            {/* Connected Stores List */}
            {storeConnections.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Connected Stores</h3>
                {storeConnections.map((connection) => (
                  <div key={connection.id} className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                    <div className="px-6 py-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 ${getPlatformColor(connection.platform)} rounded-lg flex items-center justify-center text-2xl`}>
                            {getPlatformIcon(connection.platform)}
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900">{connection.store_name}</h3>
                            <p className="text-sm text-slate-600 capitalize">{connection.platform}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                            connection.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : connection.status === 'error'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {connection.status === 'active' ? '✓ Connected' : 
                             connection.status === 'error' ? '✗ Error' : '⏳ Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="px-6 py-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-slate-600">
                            {connection.last_sync_at && (
                              <p>Last synced: {new Date(connection.last_sync_at).toLocaleString()}</p>
                            )}
                            {connection.orders_synced_count > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                {connection.orders_synced_count} orders synced
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleTestConnection(connection.id)}
                              disabled={loading}
                              className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                            >
                              Test
                            </button>
                            <button
                              onClick={() => handleSyncStore(connection.id, connection.store_name)}
                              disabled={loading}
                              className="px-4 py-2 text-sm font-medium text-brand-blue border border-brand-blue rounded-lg hover:bg-blue-50 disabled:opacity-50"
                            >
                              Sync Now
                            </button>
                            {connection.platform === 'shopify' && (
                              <button
                                onClick={() => handleResetWebhooks(connection.id, connection.store_name)}
                                disabled={loading}
                                className="px-4 py-2 text-sm font-medium text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50"
                                title="Reset webhooks if real-time sync is failing"
                              >
                                Reset Webhooks
                              </button>
                            )}
                            <button
                              onClick={() => handleDisconnectStore(connection.id, connection.store_name)}
                              disabled={loading}
                              className="px-4 py-2 text-sm font-medium text-red-600 border border-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                            >
                              Disconnect
                            </button>
                          </div>
                        </div>

                        {connection.platform === 'faire' && (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium text-slate-800">Faire order import source</p>
                                <p className="text-xs text-slate-500">
                                  Choose whether to pull Faire orders from Shopify (recommended) or directly from Faire.
                                </p>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    className="text-purple-600 focus:ring-purple-500"
                                    checked={(connection.sync_settings?.orders_source || 'shopify') === 'shopify'}
                                    onChange={() => handleUpdateFaireOrdersSource(connection.id, 'shopify')}
                                    disabled={loading}
                                  />
                                  <span className="text-sm text-slate-800">Shopify (recommended)</span>
                                </label>
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    className="text-purple-600 focus:ring-purple-500"
                                    checked={connection.sync_settings?.orders_source === 'faire'}
                                    onChange={() => handleUpdateFaireOrdersSource(connection.id, 'faire')}
                                    disabled={loading}
                                  />
                                  <span className="text-sm text-slate-800">Faire direct</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Store Connections */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Add Store Connection</h3>
                {storeConnections.length > 0 && (
                  <span className="text-sm text-slate-500">
                    Connect additional stores to manage all orders in one place
                  </span>
                )}
              </div>

              {/* Shopify OAuth */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <ShoppingCartIcon className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">Shopify</h2>
                        <p className="text-sm text-slate-600">Connect with one click via OAuth</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowShopifyConnect(!showShopifyConnect)}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                    >
                      {showShopifyConnect ? 'Cancel' : storeConnections.some(s => s.platform === 'shopify') ? '+ Add Another Shopify Store' : '+ Connect Shopify'}
                    </button>
                  </div>
                </div>

                {showShopifyConnect && (
                  <div className="px-6 py-6 space-y-4 bg-slate-50">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Shopify Store Domain
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={shopifyDomain}
                          onChange={(e) => setShopifyDomain(e.target.value)}
                          placeholder="mystore"
                          className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <span className="flex items-center text-sm text-slate-500">.myshopify.com</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Enter your store name (e.g., "peachstatecandlesupply" if your URL is peachstatecandlesupply.myshopify.com)
                      </p>
                    </div>
                    <button
                      onClick={handleConnectShopify}
                      disabled={loading || !shopifyDomain}
                      className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Redirecting...' : 'Connect with Shopify →'}
                    </button>
                  </div>
                )}
              </div>

              {/* Faire */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <BuildingStorefrontIcon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">Faire</h2>
                        <p className="text-sm text-slate-600">Connect your wholesale marketplace</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowFaireForm(!showFaireForm)}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                    >
                      {showFaireForm ? 'Cancel' : '+ Connect Faire'}
                    </button>
                  </div>
                </div>

                {showFaireForm && (
                  <div className="px-6 py-6 space-y-4 bg-slate-50">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="text-sm text-purple-900 font-medium">How to get your Faire Access Token:</p>
                      <ol className="text-xs text-purple-700 mt-2 space-y-1 list-decimal list-inside">
                        <li>Log into your <a href="https://www.faire.com/brand-portal" target="_blank" rel="noopener noreferrer" className="underline">Faire Brand Portal</a></li>
                        <li>Go to Settings → Developer / API</li>
                        <li>Generate a new access token</li>
                        <li>Copy and paste it below</li>
                      </ol>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Access Token *
                      </label>
                      <input
                        type="password"
                        value={faireAccessToken}
                        onChange={(e) => setFaireAccessToken(e.target.value)}
                        placeholder="Paste your Faire access token"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Brand Name (optional)
                      </label>
                      <input
                        type="text"
                        value={faireBrandName}
                        onChange={(e) => setFaireBrandName(e.target.value)}
                        placeholder="Your brand name on Faire"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    {hasActiveShopify ? (
                      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
                        <p className="text-sm font-medium text-slate-800">Where should Faire orders import from?</p>
                        <p className="text-xs text-slate-500">
                          Shopify is recommended so orders stay consistent with your Shopify store; choose Faire direct if you do not use Faire→Shopify sync.
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              className="text-purple-600 focus:ring-purple-500"
                              checked={faireOrdersSource === 'shopify'}
                              onChange={() => setFaireOrdersSource('shopify')}
                            />
                            <span className="text-sm text-slate-800">Shopify (recommended)</span>
                          </label>
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              className="text-purple-600 focus:ring-purple-500"
                              checked={faireOrdersSource === 'faire'}
                              onChange={() => setFaireOrdersSource('faire')}
                            />
                            <span className="text-sm text-slate-800">Faire direct</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-800">
                          Shopify is not connected, so we’ll import Faire orders directly from Faire. Connect Shopify first if you prefer Shopify-sourced orders.
                        </p>
                      </div>
                    )}
                    <button
                      onClick={handleConnectFaire}
                      disabled={loading || !faireAccessToken}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {loading ? 'Connecting...' : 'Connect Faire'}
                    </button>
                  </div>
                )}
              </div>

              {/* WooCommerce API Keys */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center text-2xl">
                        🔌
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">WooCommerce</h2>
                        <p className="text-sm text-slate-600">Connect with API keys</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowWooCommerceForm(!showWooCommerceForm)}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >
                      {showWooCommerceForm ? 'Cancel' : '+ Connect WooCommerce'}
                    </button>
                  </div>
                </div>

                {showWooCommerceForm && (
                  <div className="px-6 py-6 space-y-4 bg-slate-50">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Store URL *
                      </label>
                      <input
                        type="url"
                        value={wooStoreUrl}
                        onChange={(e) => setWooStoreUrl(e.target.value)}
                        placeholder="https://mystore.com"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Consumer Key *
                      </label>
                      <input
                        type="text"
                        value={wooConsumerKey}
                        onChange={(e) => setWooConsumerKey(e.target.value)}
                        placeholder="ck_..."
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Consumer Secret *
                      </label>
                      <input
                        type="password"
                        value={wooConsumerSecret}
                        onChange={(e) => setWooConsumerSecret(e.target.value)}
                        placeholder="cs_..."
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Get from WooCommerce → Settings → Advanced → REST API → Add Key
                      </p>
                    </div>
                    <button
                      onClick={handleConnectWooCommerce}
                      disabled={loading || !wooStoreUrl || !wooConsumerKey || !wooConsumerSecret}
                      className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Connecting...' : 'Connect WooCommerce'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Wallet & Payments Section */}
        {activeSection === 'wallet' && (
          <div className="space-y-6">
            {/* Wallet Balance */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-6">
                <div className="text-center">
                  <p className="text-sm text-slate-600 mb-2">Current Balance</p>
                  <p className={`text-5xl font-bold mb-4 ${
                    walletBalance > 50 ? 'text-green-600' :
                    walletBalance > 20 ? 'text-orange-600' :
                    'text-red-600'
                  }`}>
                    ${walletBalance.toFixed(2)}
                  </p>
                  <button
                    onClick={() => setShowAddFundsModal(true)}
                    className="px-8 py-3 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover"
                  >
                    + Add Funds
                  </button>
                  {walletBalance < 20 && (
                    <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-sm text-orange-900 font-medium">⚠️ Low Balance Warning</p>
                      <p className="text-xs text-orange-700 mt-1">
                        Add funds to ensure you can create USPS labels without interruption.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Transaction History */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Transaction History</h2>
                <p className="text-sm text-slate-600 mt-1">Recent wallet activity</p>
              </div>
              <div className="px-6 py-6">
                <p className="text-sm text-slate-500 text-center py-8">
                  Transaction history view coming soon
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Packages Section */}
        {activeSection === 'packages' && (
          <PackageSettings />
        )}

        {/* International Section */}
        {activeSection === 'international' && (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <GlobeAltIcon className="w-4 h-4 text-amber-700" />
                <p className="text-sm text-amber-900 font-medium">International Shipping Settings</p>
              </div>
              <p className="text-xs text-amber-700 mt-1">
                Configure customs defaults and tax identifiers for international shipments. HS codes should be set on each product.
              </p>
            </div>

            {customsLoading ? (
              <div className="text-center py-8">
                <p className="text-slate-600">Loading customs settings...</p>
              </div>
            ) : customsSettings && (
              <>
                {/* Default Customs Settings */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">Default Customs Settings</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      These defaults apply to all international shipments
                    </p>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Package Contents Type
                        </label>
                        <select
                          value={customsSettings.default_contents}
                          onChange={(e) => handleUpdateCustomsSettings({ default_contents: e.target.value as CustomsSettings['default_contents'] })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        >
                          <option value="merchandise">Merchandise</option>
                          <option value="gift">Gift</option>
                          <option value="documents">Documents</option>
                          <option value="sample">Sample</option>
                          <option value="returned_goods">Returned Goods</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Non-Delivery Action
                        </label>
                        <select
                          value={customsSettings.default_non_delivery}
                          onChange={(e) => handleUpdateCustomsSettings({ default_non_delivery: e.target.value as CustomsSettings['default_non_delivery'] })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        >
                          <option value="return_to_sender">Return to Sender</option>
                          <option value="treat_as_abandoned">Treat as Abandoned</option>
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          What should happen if the package cannot be delivered
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Terms of Trade (Duties)
                        </label>
                        <select
                          value={customsSettings.default_terms_of_trade}
                          onChange={(e) => handleUpdateCustomsSettings({ default_terms_of_trade: e.target.value as CustomsSettings['default_terms_of_trade'] })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        >
                          <option value="ddu">DDU - Recipient pays duties (most common)</option>
                          <option value="ddp">DDP - You pay duties upfront</option>
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          DDU = Delivered Duties Unpaid, DDP = Delivered Duties Paid
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Default HS Tariff Code
                        </label>
                        <input
                          type="text"
                          value={customsSettings.default_hs_tariff_code || ''}
                          onChange={(e) => handleUpdateCustomsSettings({ default_hs_tariff_code: e.target.value.replace(/[^0-9.]/g, '') })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                          placeholder="340600"
                          maxLength={12}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Used when a product has no per-product HS code. 340600 = candles.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Default Country of Origin
                        </label>
                        <input
                          type="text"
                          value={customsSettings.default_country_of_origin || ''}
                          onChange={(e) => handleUpdateCustomsSettings({ default_country_of_origin: e.target.value.toUpperCase().slice(0, 2) })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue uppercase"
                          placeholder="US"
                          maxLength={2}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          ISO 2-letter code. Applied when a product has no origin set.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Default Item Description
                        </label>
                        <input
                          type="text"
                          value={customsSettings.default_item_description || ''}
                          onChange={(e) => handleUpdateCustomsSettings({ default_item_description: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                          placeholder="Scented soy wax candle for home fragrance use"
                          maxLength={100}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Fallback description for items without a title. SKU is appended automatically.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Customs Declaration Statement
                      </label>
                      <textarea
                        value={customsSettings.declaration}
                        onChange={(e) => handleUpdateCustomsSettings({ declaration: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        rows={3}
                        placeholder="I hereby certify that the information on this invoice is true and correct..."
                      />
                    </div>
                  </div>
                </div>

                {/* Tax Identifiers */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Tax Identifiers</h3>
                        <p className="text-sm text-slate-600 mt-1">
                          VAT, EORI, IOSS, and other tax numbers for EU/UK/AU shipments
                        </p>
                      </div>
                      <button
                        onClick={() => setShowAddTaxId(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover"
                      >
                        + Add Tax ID
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    {customsSettings.tax_identifiers.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <p>No tax identifiers configured</p>
                        <p className="text-sm mt-1">Add VAT, EORI, or IOSS numbers for international shipments</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {customsSettings.tax_identifiers.map((ti) => (
                          <div key={ti.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                            <div>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-2">
                                {ti.type.toUpperCase()}
                              </span>
                              <span className="font-mono font-medium text-slate-900">{ti.value}</span>
                              <span className="text-sm text-slate-500 ml-2">
                                ({ti.issuing_authority} - {ti.entity_type})
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemoveTaxIdentifier(ti.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* HS Code Resources */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">HS Code Resources</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Look up Harmonized System tariff codes for your products
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <a
                        href="https://hts.usitc.gov/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <p className="font-medium text-blue-900">🇺🇸 US HTS</p>
                        <p className="text-sm text-blue-700 mt-1">Official US Harmonized Tariff Schedule</p>
                      </a>
                      <a
                        href="https://ec.europa.eu/taxation_customs/dds2/taric/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <p className="font-medium text-blue-900">🇪🇺 EU TARIC</p>
                        <p className="text-sm text-blue-700 mt-1">European Union tariff database</p>
                      </a>
                      <a
                        href="https://www.tariffnumber.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <p className="font-medium text-blue-900">🌐 TariffNumber.com</p>
                        <p className="text-sm text-blue-700 mt-1">Multi-country HS code lookup</p>
                      </a>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Add Tax ID Modal */}
            {showAddTaxId && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                  <div className="p-6 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-slate-900">Add Tax Identifier</h2>
                      <button onClick={() => setShowAddTaxId(false)} className="text-slate-400 hover:text-slate-600">
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                      <select
                        value={newTaxId.type}
                        onChange={(e) => setNewTaxId({ ...newTaxId, type: e.target.value as TaxIdentifier['type'] })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      >
                        <option value="vat">VAT Number</option>
                        <option value="eori">EORI Number</option>
                        <option value="ioss">IOSS Number (EU)</option>
                        <option value="voec">VOEC Number (Norway)</option>
                        <option value="oss">OSS Number</option>
                        <option value="abn">ABN (Australia)</option>
                        <option value="ein">EIN (US)</option>
                        <option value="tin">TIN</option>
                        <option value="ssn">SSN</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Value</label>
                      <input
                        type="text"
                        value={newTaxId.value}
                        onChange={(e) => setNewTaxId({ ...newTaxId, value: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        placeholder="e.g., DE123456789"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Issuing Country</label>
                      <input
                        type="text"
                        value={newTaxId.issuing_authority}
                        onChange={(e) => setNewTaxId({ ...newTaxId, issuing_authority: e.target.value.toUpperCase().substring(0, 2) })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        placeholder="e.g., DE, GB, FR"
                        maxLength={2}
                      />
                      <p className="text-xs text-slate-500 mt-1">2-letter country code</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
                      <select
                        value={newTaxId.entity_type}
                        onChange={(e) => setNewTaxId({ ...newTaxId, entity_type: e.target.value as 'shipper' | 'recipient' })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      >
                        <option value="shipper">Shipper (Your business)</option>
                        <option value="recipient">Recipient</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-6 border-t border-slate-200 flex justify-end space-x-3">
                    <button
                      onClick={() => setShowAddTaxId(false)}
                      className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddTaxIdentifier}
                      className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg"
                    >
                      Add Tax ID
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Printers Section */}
        {activeSection === 'printers' && (
          <div className="space-y-6">
            {/* QZ Tray Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <PrinterIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-blue-900">Direct Printing with QZ Tray</h3>
                  <p className="text-sm text-blue-800 mt-1">
                    QZ Tray allows you to print shipping labels directly to your label printer without a print dialog.
                    Settings are saved on this computer only - each workstation can have its own printer configured.
                  </p>
                  <a 
                    href="https://qz.io/download/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2 font-medium"
                  >
                    Download QZ Tray →
                  </a>
                </div>
              </div>
            </div>

            {/* Desktop Print Agent (Beta) */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Desktop Print Agent (Beta)</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Install the desktop agent (Windows/macOS) and pair once to print without popups. Use alongside or instead of QZ Tray.
                    </p>
                    <div className="text-sm text-blue-700 mt-2">
                      <a
                        href={process.env.REACT_APP_PRINT_AGENT_DOWNLOAD || 'https://example.com/print-agent-download'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:text-blue-900"
                      >
                        Download agent
                      </a>
                      {!process.env.REACT_APP_PRINT_AGENT_DOWNLOAD && (
                        <span className="text-slate-500 text-xs ml-2">(placeholder link)</span>
                      )}
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <span>Print mode</span>
                    <select
                      className="rounded border-slate-300 text-sm focus:ring-brand-blue"
                      value={printMode}
                      onChange={(e) => {
                        const mode = e.target.value === 'agent' ? 'agent' : 'qz';
                        setPrintMode(mode as 'qz' | 'agent');
                        const newSettings = { ...printerSettings, usePrintAgent: mode === 'agent' };
                        setPrinterSettings(newSettings);
                        printerService.savePrinterSettings(newSettings);
                      }}
                    >
                      <option value="agent">Desktop Print Agent</option>
                      <option value="qz">QZ Tray</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="flex flex-wrap gap-3 items-center">
                  <button
                    onClick={handleRequestPairingCode}
                    disabled={pairingLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
                  >
                    {pairingLoading ? 'Requesting...' : 'Request Pairing Code'}
                  </button>
                  {pairingCode && (
                    <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                      Code: <span className="font-mono font-semibold">{pairingCode}</span>
                      {pairingExpiresAt && (
                        <span className="ml-2 text-xs text-blue-700">
                          Expires {new Date(pairingExpiresAt).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-800">Paired devices</h3>
                    <button
                      onClick={loadPrintDevices}
                      disabled={devicesLoading}
                      className="text-sm text-brand-blue hover:text-brand-blue-hover"
                    >
                      {devicesLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                  </div>
                  {devicesLoading ? (
                    <p className="text-sm text-slate-600">Loading devices...</p>
                  ) : printDevices.length === 0 ? (
                    <p className="text-sm text-slate-600">No devices yet. Generate a pairing code to add one.</p>
                  ) : (
                    <div className="space-y-3">
                      {printDevices.map((device) => {
                        const printers = Array.isArray(device.printers) ? device.printers : [];
                        const isSelected = printerSettings.agentDeviceId === device.id;
                        return (
                          <div
                            key={device.id}
                            className="p-3 rounded-lg border border-slate-200 bg-white flex flex-col gap-2"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-slate-900">{device.friendly_name || 'Device'}</p>
                                <p className="text-xs text-slate-500">
                                  {device.platform || 'Unknown'} • Last seen{' '}
                                  {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'never'}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs rounded ${device.last_seen ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {device.last_seen ? 'Online recently' : 'Offline'}
                                </span>
                                <button
                                  onClick={() => handleSelectAgentDevice(device.id, printers[0]?.name || null)}
                                  className={`px-3 py-1.5 text-xs font-medium rounded ${
                                    isSelected ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                                  }`}
                                >
                                  {isSelected ? 'Default for labels' : 'Use for labels'}
                                </button>
                                <button
                                  onClick={() => handleRevokeDevice(device.id)}
                                  className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700"
                                >
                                  Revoke
                                </button>
                              </div>
                            </div>
                            {printers.length > 0 && (
                              <div className="flex items-center gap-2 text-sm text-slate-700 flex-wrap">
                                <span className="text-xs uppercase text-slate-500">Printers:</span>
                                <select
                                  value={isSelected ? printerSettings.agentPrinterName || '' : ''}
                                  onChange={(e) => handleSelectAgentDevice(device.id, e.target.value || null)}
                                  className="px-3 py-1.5 text-sm rounded border border-slate-200"
                                  disabled={!isSelected}
                                >
                                  <option value="">Any</option>
                                  {printers.map((p: any) => (
                                    <option key={p.name || p} value={p.name || p}>
                                      {p.name || p}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={handleTestAgentPrint}
                    disabled={agentTestLoading || !printerSettings.agentDeviceId}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {agentTestLoading ? 'Sending test...' : 'Send test print to agent'}
                  </button>
                  {!printerSettings.agentDeviceId && (
                    <span className="text-xs text-slate-500">Select a paired device above to enable test</span>
                  )}
                </div>
                </div>
              </div>
            </div>

            {/* Connection Status */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Connection Status</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      QZ Tray must be running on this computer for direct printing
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                    qzConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {qzConnected ? '● Connected' : '○ Not Connected'}
                  </span>
                </div>
              </div>
              <div className="px-6 py-4">
                <button
                  onClick={async () => {
                    setPrinterLoading(true);
                    setPrinterError(null);
                    const timeoutMs = 25000;
                    const timeoutPromise = new Promise<never>((_, reject) =>
                      setTimeout(() => reject(new Error('Connection timed out. Check that QZ Tray is running and you allowed the connection.')), timeoutMs)
                    );
                    try {
                      await Promise.race([printerService.connectToQz(), timeoutPromise]);
                      setQzConnected(printerService.isQzConnected());
                      const printerList = await Promise.race([
                        printerService.getPrinters(),
                        timeoutPromise,
                      ]);
                      setPrinters(printerList);
                    } catch (error: any) {
                      setPrinterError(error?.message || 'Connection failed');
                      setQzConnected(printerService.isQzConnected());
                    } finally {
                      setPrinterLoading(false);
                    }
                  }}
                  disabled={printerLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50 flex items-center gap-2"
                >
                  {printerLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Connecting...
                    </>
                  ) : (
                    <>
                      {qzConnected ? 'Refresh Printers' : 'Connect to QZ Tray'}
                    </>
                  )}
                </button>
                {printerError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{printerError}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Label Printer Selection */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Label Printer</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Select the printer to use for shipping labels on this computer
                </p>
              </div>
              <div className="px-6 py-4 space-y-4">
                {printers.length > 0 ? (
                  <>
                    <select
                      value={printerSettings.labelPrinter || ''}
                      onChange={(e) => {
                        const newSettings = { ...printerSettings, labelPrinter: e.target.value || null };
                        setPrinterSettings(newSettings);
                        printerService.savePrinterSettings(newSettings);
                      }}
                      className="w-full rounded-lg border-slate-300 focus:border-brand-blue focus:ring-brand-blue"
                    >
                      <option value="">Select a printer...</option>
                      {printers.map((printer) => (
                        <option key={printer.name} value={printer.name}>
                          {printer.name} {printer.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                    
                    {printerSettings.labelPrinter && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={async () => {
                            if (!printerSettings.labelPrinter) return;
                            setTestPrinting(true);
                            try {
                              await printerService.printTestLabel(printerSettings.labelPrinter);
                              alert('Test label sent to printer!');
                            } catch (error: any) {
                              alert('Test print failed: ' + error.message);
                            } finally {
                              setTestPrinting(false);
                            }
                          }}
                          disabled={testPrinting}
                          className="px-4 py-2 text-sm font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50 disabled:opacity-50 flex items-center gap-2"
                        >
                          {testPrinting ? 'Printing...' : '🖨️ Print Test Label'}
                        </button>
                        <span className="text-sm text-slate-500">
                          Prints a test page to verify your printer setup
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <PrinterIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No printers found</p>
                    <p className="text-sm mt-1">
                      Click "Connect to QZ Tray" above to detect printers
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Auto-Print Option */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Printing Options</h2>
              </div>
              <div className="px-6 py-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={printerSettings.autoPrint}
                    onChange={(e) => {
                      const newSettings = { ...printerSettings, autoPrint: e.target.checked };
                      setPrinterSettings(newSettings);
                      printerService.savePrinterSettings(newSettings);
                    }}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Auto-print labels after creation</p>
                    <p className="text-sm text-slate-500">
                      Automatically send labels to the printer when created (instead of clicking "Direct Print")
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Current Settings Summary */}
            {printerSettings.labelPrinter && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      Label Printer: {printerSettings.labelPrinter}
                    </p>
                    <p className="text-xs text-green-700 mt-0.5">
                      This setting is saved for this computer only
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Team Members Section */}
        {activeSection === 'team' && (
          <div className="space-y-6">
            {/* Invite User */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Invite User</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Send an invitation by email. They'll be able to sign up and join your organization.
                </p>
              </div>
              <div className="px-6 py-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder="Email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleInviteUser()}
                    className="flex-1 px-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="px-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent bg-white"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button
                    onClick={handleInviteUser}
                    disabled={inviteLoading || !inviteEmail.trim()}
                    className="px-6 py-2 bg-brand-blue text-white text-sm font-medium rounded-lg hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </div>
            </div>

            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Pending Invites</h2>
                </div>
                <div className="divide-y divide-slate-200">
                  {pendingInvites.map((invite: any) => (
                    <div key={invite.id} className="px-6 py-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{invite.email}</p>
                        <p className="text-xs text-slate-500">
                          Role: <span className="capitalize">{invite.role}</span>
                          {invite.expires_at && (
                            <> &middot; Expires {new Date(invite.expires_at).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {invite.sign_up_url && (
                          <button
                            onClick={() => handleCopyInviteLink(invite.sign_up_url)}
                            className="text-sm text-brand-blue hover:text-brand-blue-hover font-medium transition-colors"
                            title="Copy sign-up link (share manually if email didn't deliver)"
                          >
                            Copy link
                          </button>
                        )}
                        <button
                          onClick={() => handleCancelInvite(invite.id)}
                          className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current Members */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">
                  Members {!teamLoading && `(${teamMembers.length})`}
                </h2>
              </div>
              {teamLoading ? (
                <div className="px-6 py-8 text-center text-slate-500">Loading team...</div>
              ) : teamMembers.length === 0 ? (
                <div className="px-6 py-8 text-center text-slate-500">No team members found</div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {teamMembers.map((member: any) => (
                    <div key={member.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-full bg-brand-blue-lighter flex items-center justify-center">
                          <span className="text-sm font-semibold text-brand-blue">
                            {(member.name || member.email || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {member.name || 'Unnamed User'}
                          </p>
                          <p className="text-xs text-slate-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          member.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                          member.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          member.role === 'viewer' ? 'bg-slate-100 text-slate-600' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {member.role}
                        </span>
                        <span className="text-xs text-slate-400">
                          Joined {new Date(member.joined_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Advanced Section */}
        {activeSection === 'advanced' && (
          <div className="space-y-6">
            {/* Account Timezone */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Account Timezone</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      All users and workstations will see times in this timezone
                    </p>
                  </div>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-brand-blue-lighter text-brand-blue">
                    {orgTimezone.replace(/_/g, ' ').split('/').pop()}
                  </span>
                </div>
              </div>
              <div className="px-6 py-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Account-wide setting:</strong> Unlike computer-based time, this ensures all staff see the same times regardless of their device or location.
                    Order timestamps, SLA deadlines, and reports will all use this timezone.
                  </p>
                </div>
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Timezone
                    </label>
                    <select
                      value={orgTimezone}
                      onChange={(e) => handleSaveOrgTimezone(e.target.value)}
                      disabled={orgTimezoneLoading}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white disabled:opacity-50"
                    >
                      <optgroup label="United States">
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Phoenix">Arizona (no DST)</option>
                        <option value="America/Anchorage">Alaska Time (AKT)</option>
                        <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                        <option value="America/Detroit">Detroit</option>
                        <option value="America/Indianapolis">Indianapolis</option>
                        <option value="America/Boise">Boise (Mountain)</option>
                      </optgroup>
                      <optgroup label="Americas">
                        <option value="America/Toronto">Toronto</option>
                        <option value="America/Vancouver">Vancouver</option>
                        <option value="America/Mexico_City">Mexico City</option>
                      </optgroup>
                      <optgroup label="Europe">
                        <option value="Europe/London">London (GMT/BST)</option>
                        <option value="Europe/Paris">Paris (CET)</option>
                        <option value="Europe/Berlin">Berlin (CET)</option>
                        <option value="Europe/Amsterdam">Amsterdam (CET)</option>
                      </optgroup>
                      <optgroup label="Asia Pacific">
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                        <option value="Asia/Shanghai">Shanghai (CST)</option>
                        <option value="Asia/Singapore">Singapore (SGT)</option>
                        <option value="Asia/Dubai">Dubai (GST)</option>
                        <option value="Australia/Sydney">Sydney (AEST)</option>
                        <option value="Australia/Melbourne">Melbourne (AEST)</option>
                        <option value="Pacific/Auckland">Auckland (NZST)</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="UTC">UTC (Coordinated Universal Time)</option>
                      </optgroup>
                    </select>
                  </div>
                  {orgTimezoneLoading && (
                    <span className="text-sm text-slate-500">Saving...</span>
                  )}
                </div>
              </div>
            </div>

            {/* Max Package Weight */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Max Package Weight</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Enforce an upper weight limit per package to avoid overweight charges. Applies to new shipments and reships.
                    </p>
                  </div>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    Current: {maxPackageWeightLbs?.toString() || '—'} lb
                  </span>
                </div>
              </div>
              <div className="px-6 py-6 space-y-3">
                <label className="block text-sm font-medium text-slate-700">
                  Max package weight (lb)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    step={0.5}
                    value={maxPackageWeightLbs}
                    onChange={(e) => setMaxPackageWeightLbs(parseFloat(e.target.value))}
                    className="w-40 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                  />
                  <button
                    onClick={handleSaveMaxPackageWeight}
                    disabled={maxPackageWeightSaving}
                    className="px-4 py-2 text-sm font-semibold text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
                  >
                    {maxPackageWeightSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Default is 50 lb. If a box exceeds this limit, shipping will be blocked with an error asking to split packages.
                </p>
              </div>
            </div>

            {/* SLA Monitoring */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">SLA Monitoring</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Service Level Agreement thresholds for order processing
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                    slaEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {slaEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div className="px-6 py-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="sla-enabled"
                    checked={slaEnabled}
                    onChange={(e) => setSlaEnabled(e.target.checked)}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                  <label htmlFor="sla-enabled" className="text-sm text-slate-700">
                    Enable SLA monitoring and notifications
                  </label>
                </div>

                {/* Business Hours Toggle */}
                <div className="flex items-center space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="use-business-hours"
                    checked={useBusinessHours}
                    onChange={(e) => setUseBusinessHours(e.target.checked)}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                  <label htmlFor="use-business-hours" className="text-sm text-slate-700">
                    Use business hours only (excludes nights, weekends, and holidays)
                  </label>
                </div>

                {useBusinessHours && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-blue-900 mb-2">Business Days</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { day: 0, label: 'Sun' },
                          { day: 1, label: 'Mon' },
                          { day: 2, label: 'Tue' },
                          { day: 3, label: 'Wed' },
                          { day: 4, label: 'Thu' },
                          { day: 5, label: 'Fri' },
                          { day: 6, label: 'Sat' },
                        ].map(({ day, label }) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => {
                              if (businessDays.includes(day)) {
                                setBusinessDays(businessDays.filter(d => d !== day));
                              } else {
                                setBusinessDays([...businessDays, day].sort());
                              }
                            }}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                              businessDays.includes(day)
                                ? 'bg-brand-blue text-white'
                                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-2">
                          Business Hours Start
                        </label>
                        <input
                          type="time"
                          value={businessStartTime}
                          onChange={(e) => setBusinessStartTime(e.target.value)}
                          className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-2">
                          Business Hours End
                        </label>
                        <input
                          type="time"
                          value={businessEndTime}
                          onChange={(e) => setBusinessEndTime(e.target.value)}
                          className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Timezone
                      </label>
                      <select
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                      >
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Anchorage">Alaska Time (AKT)</option>
                        <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                      </select>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <p className="text-xs text-blue-800">
                        <strong>Example:</strong> With {businessDays.length > 0 ? businessDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ') : 'no days selected'} business days 
                        from {businessStartTime} to {businessEndTime}, that's{' '}
                        <strong>
                          {(() => {
                            const [startH, startM] = businessStartTime.split(':').map(Number);
                            const [endH, endM] = businessEndTime.split(':').map(Number);
                            const hoursPerDay = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
                            return hoursPerDay > 0 ? hoursPerDay : 0;
                          })()} hours/day
                        </strong>.
                        A {shippingThreshold}-hour SLA = approximately{' '}
                        <strong>
                          {(() => {
                            const [startH, startM] = businessStartTime.split(':').map(Number);
                            const [endH, endM] = businessEndTime.split(':').map(Number);
                            const hoursPerDay = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
                            if (hoursPerDay <= 0) return '0';
                            return (shippingThreshold / hoursPerDay).toFixed(1);
                          })()} business days
                        </strong>.
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Shipping Orders Threshold ({useBusinessHours ? 'business ' : ''}hours)
                    </label>
                    <input
                      type="number"
                      value={shippingThreshold}
                      onChange={(e) => setShippingThreshold(parseInt(e.target.value) || 0)}
                      min="0"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Alert if not shipped within this time
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Pickup Orders Threshold ({useBusinessHours ? 'business ' : ''}hours)
                    </label>
                    <input
                      type="number"
                      value={pickupThreshold}
                      onChange={(e) => setPickupThreshold(parseInt(e.target.value) || 0)}
                      min="0"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Alert if not ready within this time
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleSaveSLA}
                  disabled={loading}
                  className="px-6 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save SLA Settings'}
                </button>
              </div>
            </div>

            {/* Inventory Alerts */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Inventory Alerts</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Get notified when products are running low on stock
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                    inventoryAlertsEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {inventoryAlertsEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div className="px-6 py-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="inventory-alerts-enabled"
                    checked={inventoryAlertsEnabled}
                    onChange={(e) => setInventoryAlertsEnabled(e.target.checked)}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                  <label htmlFor="inventory-alerts-enabled" className="text-sm text-slate-700">
                    Enable low stock alerts and notifications
                  </label>
                </div>

                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Low Stock Threshold (units)
                  </label>
                  <input
                    type="number"
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Alert when a product's inventory falls to or below this number
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>How it works:</strong> CraftyShip checks inventory levels every 15 minutes and during product syncs.
                    When a product drops to or below the threshold, it appears in the inventory alert badge
                    (the box icon in the top bar). You can also add stock directly from Shopify right from the alert panel.
                  </p>
                </div>

                <button
                  onClick={handleSaveInventorySettings}
                  disabled={loading}
                  className="px-6 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Inventory Settings'}
                </button>
              </div>
            </div>

            {/* Emergency Void by Tracking Number */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Emergency Void by Tracking Number</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Void orphaned labels directly with carrier (for troubleshooting only)
                    </p>
                  </div>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    ⚠️ Admin Tool
                  </span>
                </div>
              </div>
              <div className="px-6 py-6 space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-900 font-medium">⚠️ Use with caution</p>
                  <p className="text-xs text-orange-700 mt-1">
                    This tool voids labels directly with UPS/USPS without updating shipment records. Only use for orphaned labels that can't be voided through normal workflow.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Tracking Number
                    </label>
                    <input
                      type="text"
                      value={voidTrackingNumber}
                      onChange={(e) => setVoidTrackingNumber(e.target.value)}
                      placeholder="1Z9Y4V730306751073"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Carrier
                    </label>
                    <select
                      value={voidCarrier}
                      onChange={(e) => setVoidCarrier(e.target.value as 'ups' | 'usps')}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    >
                      <option value="ups">UPS</option>
                      <option value="usps">USPS</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={handleEmergencyVoid}
                  disabled={loading || !voidTrackingNumber}
                  className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Voiding...' : 'Void Label with Carrier'}
                </button>
              </div>
            </div>

            {/* Employee Mode */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Employee Mode</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      PIN-based login for warehouse workstations with time tracking
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                    employeeModeEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {employeeModeEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div className="px-6 py-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">Enable Employee Mode</p>
                    <p className="text-xs text-slate-500 mt-1">
                      When enabled, users must log in with a PIN to access the workstation. 
                      Includes time tracking, activity logging, and inactivity timeouts.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const newValue = !employeeModeEnabled;
                      // Optimistically update UI
                      setEmployeeModeEnabled(newValue);
                      try {
                        await organizationApi.updateSettings({ employee_mode_enabled: newValue });
                      } catch (error: any) {
                        // Revert on error
                        setEmployeeModeEnabled(!newValue);
                        console.error('Failed to update employee mode:', error);
                        
                        // Show specific error message from API
                        const errorMessage = error.response?.data?.error || 'Failed to update setting';
                        alert(errorMessage);
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 ${
                      employeeModeEnabled ? 'bg-brand-blue' : 'bg-slate-200'
                    }`}
                    role="switch"
                    aria-checked={employeeModeEnabled}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        employeeModeEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {employeeModeEnabled && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium">Employee Mode is Active</p>
                    <p className="text-xs text-blue-700 mt-1">
                      • Employees must enter their PIN to access the system<br/>
                      • Automatic lock after 10 minutes of inactivity<br/>
                      • Time tracking and audit logs are enabled<br/>
                      • Manage employees in the Employees section
                    </p>
                  </div>
                )}

                {!employeeModeEnabled && (
                  <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-sm text-slate-700 font-medium">Employee Mode is Disabled</p>
                    <p className="text-xs text-slate-600 mt-1">
                      All users can access the application without PIN login.
                      Enable this for warehouse workstations with multiple employees.
                    </p>
                    <p className="text-xs text-amber-600 mt-2 font-medium">
                      Note: You must have at least one admin employee with a PIN set up before enabling.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Funds Modal */}
        {showAddFundsModal && (
          <AddFundsModal
            currentBalance={walletBalance}
            onClose={() => setShowAddFundsModal(false)}
            onSuccess={() => {
              loadWalletBalance();
              setShowAddFundsModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
};
